import { crewListBirthText } from '@felix/domain';
import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { safeFileBase } from '../drills/report-pdf.js';
import type { CrewListRow, CrewListView } from './types.js';

/**
 * Crew List PDF'i (IMO FAL Form 5; ortak 2026-10-26 madde 4; ADR 0130 §B3). Ortağın isteği: "üst
 * bilgi ve alt bilgi olmasına gerek yok, düz formatta". Şirket logosu, doküman numarası, revizyon,
 * web ve telefon yok; yalnız formun kendisi:
 *
 * - Başlık "CREW LIST", Arrival / Departure işareti, sayfa numarası (formun alanı).
 * - 1.1 Name of ship · 1.2 IMO number · 1.3 Call sign · 1.4 Voyage number · 2 Port of
 *   arrival/departure · 3 Date of arrival/departure · 4 Flag State · 5 Last port of call.
 * - 6 No. · 7 Family name, given names · 8 Rank or rating · 9 Nationality · 10 Date and place of
 *   birth · 11 Nature and number of identity document; başlık satırı her sayfada yinelenir.
 * - 12 Date and signature by master, authorized agent or officer (boş, elle).
 *
 * Boş alan boş kalır: eski sistem tarih alanına "null - null" basıyordu. pdfmake istemcide,
 * gömülü yazı tipiyle internetsiz çalışır; dil arayüzün dilidir (Türkçede iki dilli başlıklar).
 */

export type CrewListPdfT = (key: string, options?: Record<string, unknown>) => string;

const GRID: CustomTableLayout = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
};

const value = (text: string | null | undefined) => text ?? '';
const day = (date: string | null | undefined) => (date ? formatDay(date) : '');

/** "Soyadı, adları": ad yoksa yalnız soyadı. */
export function crewListName(row: Pick<CrewListRow, 'familyName' | 'givenNames'>): string {
  return [row.familyName, row.givenNames].filter(Boolean).join(', ');
}

/**
 * Kutu 11'in satırları: "Passport P-478592", altında "Seaman's Book SB-2201" (04.11 notları,
 * karar 13); numara yoksa yalnız tür. Eski satırda tek satır.
 */
export function crewListDocumentLines(t: CrewListPdfT, row: CrewListRow): string[] {
  const line = (type: string, number: string | null | undefined) =>
    [t(`crewList.docType.${type}`), number].filter(Boolean).join(' ');
  return [
    line(row.docType, row.docNumber),
    ...(row.secondDocType ? [line(row.secondDocType, row.secondDocNumber)] : []),
  ];
}

/** Kutu 11 tek metin: belgeler alt alta (PDF hücresinde satır sonu). */
export function crewListDocument(t: CrewListPdfT, row: CrewListRow): string {
  return crewListDocumentLines(t, row).join('\n');
}

function field(label: string, text: string): TableCell {
  return {
    stack: [
      { text: label, fontSize: 7 },
      { text: text || ' ', fontSize: 10, bold: true, margin: [0, 2, 0, 0] },
    ],
  };
}

export function crewListPdf(t: CrewListPdfT, list: CrewListView): TDocumentDefinitions {
  const f = (key: string) => t(`crewList.fields.${key}`);
  const p = (key: string, options?: Record<string, unknown>) => t(`crewList.pdf.${key}`, options);
  const h = list.header;
  const mark = (on: boolean) => (on ? '[X]' : '[  ]');
  const content: Content[] = [
    {
      columns: [
        {
          width: '*',
          stack: [
            { text: p('title'), fontSize: 14, bold: true },
            { text: p('subtitle'), fontSize: 8, margin: [0, 1, 0, 0] },
          ],
        },
        {
          width: 'auto',
          text: `${mark(list.direction === 'arrival')} ${p('arrival')}     ${mark(list.direction === 'departure')} ${p('departure')}`,
          fontSize: 10,
          bold: true,
          margin: [0, 4, 0, 0],
        },
      ],
      margin: [0, 0, 0, 8],
    },
    {
      table: {
        widths: ['*', '*'],
        body: [
          [field(f('vesselName'), h.vesselName), field(f('imo'), value(h.imo))],
          [field(f('callSign'), value(h.callSign)), field(f('voyageNo'), value(h.voyageNo))],
          [field(f('port'), value(h.port)), field(f('date'), day(h.date))],
          [field(f('flag'), value(h.flag)), field(f('lastPort'), value(h.lastPort))],
        ],
      },
      layout: GRID,
      margin: [0, 0, 0, 8],
    },
    {
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: [22, '*', 70, 62, 88, 110],
        body: [
          [f('no'), f('name'), f('rank'), f('nationality'), f('birth'), f('document')].map(
            (text) => ({ text, bold: true, fontSize: 7 }),
          ),
          ...list.rows.map((row, index) => [
            String(index + 1),
            crewListName(row),
            value(row.rank),
            value(row.nationality),
            crewListBirthText(row.birthDate, row.birthPlace, formatDay),
            crewListDocument(t, row),
          ]),
        ],
      },
      layout: GRID,
      fontSize: 9,
    },
    {
      unbreakable: true,
      table: {
        widths: ['*'],
        heights: [56],
        body: [[{ text: p('signature'), fontSize: 8 }]],
      },
      layout: GRID,
      margin: [0, 12, 0, 0],
    },
  ];
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [36, 40, 36, 36],
    info: { title: p('title'), subject: `${h.vesselName} · ${p(list.direction)}` },
    defaultStyle: { fontSize: 9 },
    // Sayfa numarası formun alanıdır ("Page Number"); şirket üst ve alt bilgisi yok
    header: (page: number, pages: number) => ({
      text: p('page', { page, pages }),
      alignment: 'right',
      fontSize: 8,
      margin: [36, 18, 36, 0],
    }),
    content,
  };
}

/** Dosya adı: "crew-list-M-Y-BGM-01-arrival-2026-10-26.pdf" (gemide kaydedilebilir ad). */
export function crewListFileName(list: Pick<CrewListView, 'header' | 'direction'>): string {
  const parts = ['crew-list', list.header.vesselName, list.direction, list.header.date ?? ''];
  return `${safeFileBase(parts.filter(Boolean).join('-'))}.pdf`;
}
